import {forwardRef, useState} from 'react';
import {Autocomplete, CircularProgress, TextField} from '@mui/material';
import useDebounce from 'ahooks/es/useDebounce';

import {useGetEvents} from '../../apiHooks';
import {Event} from '../../types';

type Props = {
  value: Event | null;
  onChange: (value: Event | null) => void;
  label?: string;
  error?: string;
  fullWidth?: boolean;
  disabled?: boolean;
};

export const EventSelect = forwardRef<HTMLInputElement, Props>(
  ({value, onChange, label, error, fullWidth, disabled}, ref) => {
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, {wait: 400});

    const {data, isPending} = useGetEvents({
      page: 1,
      pageSize: 50,
      searchQuery: debouncedSearch || undefined,
    });

    const options = data?.items ?? [];

    // Ensure the currently selected event is always in the list
    const isValueInOptions = value
      ? options.some(o => o.id === value.id || o.eventId === value.eventId)
      : true;
    const optionsWithSelected = isValueInOptions
      ? options
      : [value!, ...options];

    return (
      <Autocomplete
        ref={ref}
        value={value}
        inputValue={inputValue}
        options={optionsWithSelected}
        loading={isPending}
        disabled={disabled}
        fullWidth={fullWidth}
        filterOptions={x => x}
        getOptionLabel={option => option.name || option.eventId}
        isOptionEqualToValue={(option, val) =>
          option.eventId === val.eventId || option.id === val.id
        }
        onInputChange={(_, newInput, reason) => {
          setInputValue(newInput);
          if (reason === 'input') setSearchQuery(newInput);
          else setSearchQuery('');
        }}
        onChange={(_, newValue) => onChange(newValue)}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            error={!!error}
            helperText={error}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isPending && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    );
  },
);
